import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import DecisionBrief from '@/components/decisions/DecisionBrief';
import { useLocalization } from '@/hooks/useLocalization';

interface PendingAiOutput {
  id: string;
  title: string;
  summary: string | null;
  severity: string;
  status: string;
  created_at: string;
}

const STATUS_LABEL: Record<string, string> = {
  pending_review: 'Pending Review',
  more_evidence_requested: 'More Evidence Requested',
};

// Work queue only — no new rule/table. Reuses the same ai_outputs query
// EngineeringCommandCenter used ("what needs a human decision"), just
// widened to also surface items sent back for more evidence.
const ActionsRequired = () => {
  const { tx, t } = useLocalization();
  const { tenantId } = useAuth();
  const [items, setItems] = useState<PendingAiOutput[]>([]);
  const [loading, setLoading] = useState(true);
  const [openBriefId, setOpenBriefId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);

    const query = supabase
      .from('ai_outputs')
      .select('id, title, summary, severity, status, created_at')
      .eq('source_module', 'evaluate-risk-rules')
      .in('status', ['pending_review', 'more_evidence_requested'])
      .order('created_at', { ascending: false });
    if (tenantId) query.eq('tenant_id', tenantId);
    const { data } = await query;
    setItems((data ?? []) as PendingAiOutput[]);

    setLoading(false);
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('Actions Required')}</CardTitle>
              <CardDescription>{t('AI-generated recommendations awaiting a human decision')}</CardDescription>
            </div>
            <Badge variant="outline">{items.length}  {t('awaiting')}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle2 className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">{t('No open decisions — nothing currently requires action.')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {items.map((item) => (
                <div key={item.id} className="border border-border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    {item.severity === 'high' && <AlertTriangle className="h-4 w-4 text-amber-500" />}
                    <h4 className="font-medium text-foreground">{tx(item.title)}</h4>
                  </div>
                  {item.summary && <p className="text-sm text-muted-foreground mb-2">{item.summary}</p>}
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary" className="text-xs">
                      {STATUS_LABEL[item.status] ?? tx(item.status)}
                    </Badge>
                    <Button size="sm" onClick={() => setOpenBriefId(item.id)}>{t('Review Recommendation')}</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <DecisionBrief
        aiOutputId={openBriefId}
        open={openBriefId !== null}
        onOpenChange={(open) => { if (!open) setOpenBriefId(null); }}
        onDecided={() => { setOpenBriefId(null); load(); }}
      />
    </div>
  );
};

export default ActionsRequired;
